/**
 * SITE.THUNK.TS
 * Custom endpoints and actions
 */
import { createAsyncThunk } from "@reduxjs/toolkit"
import { Site, SiteRing } from "./site.types"
import siteStore from "./site.store"
import { RootState } from "@/core/root-reducer"
import { fetchBackend } from "@/core/apis/backend"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { MIN_AREA, subtractRing, totalArea } from "@/utils/site-geometry.utils"

/**
 * Add population for one site
 */
export const addPopulation = createAsyncThunk(
  "sites/add-population",
  async ({ PopulationId, SiteId }: { PopulationId: string; SiteId: string }, { dispatch, getState }) => {
    const state = getState() as RootState
    dispatch(siteStore.actions.setStatus("STATUS_SAVING"))

    //Send data
    const response = await fetchBackend({
      url: "/site/add-population",
      method: "POST",
      data: {
        PopulationId,
        SiteId
      }
    })

    //Handle response
    dispatch(siteStore.actions.setStatus("STATUS_SAVED"))
    if (response.error) {
      return false
    }

    //Update the store
    const site = state.site.list.find((x) => x.id === SiteId)
    if (site) {
      dispatch(
        siteStore.actions.setList(
          state.site.list.map((item) =>
            item.id === SiteId
              ? { ...item, populationIds: item.populationIds.concat([PopulationId]) }
              : item
          )
        )
      )
    }

    return true
  }
)

/**
 * Add risk for one site
 */
export const addRisk = createAsyncThunk("sites/add-risk", async (risk: PhysicalRisk, { dispatch, getState }) => {
  const state = getState() as RootState
  const activeSite = state.site.active

  //Save in database
  dispatch(siteStore.actions.setStatus("STATUS_SAVING"))
  const response = await fetchBackend({
    url: "/site/add-risk",
    method: "POST",
    data: {
      RiskId: risk.id,
      SiteId: activeSite.id,
      photoId: risk.photoId
    }
  })

  //Handle error
  if (response.error) {
    dispatch(siteStore.actions.setStatus("STATUS_SAVED"))
    return false
  }

  //Replace site by existing
  dispatch(siteStore.actions.replace(response))
  dispatch(physicalRiskStore.actions.setActive(response.PhysicalRisks.find((x) => x.id === risk.id)))

  return true
})

/**
 * Select site
 */
export const select = createAsyncThunk("sites/select", async (site: Site | null, { dispatch }) => {
  dispatch(siteStore.actions.setActive(site))
  dispatch(physicalRiskStore.actions.setActive(null))
})

/**
 * Resolve overlaps
 * La zone (topRing) vient d'être dessinée/modifiée "au-dessus" : on découpe
 * (différence booléenne) toutes les autres zones de la map pour supprimer
 * tout chevauchement. Une zone entièrement recouverte est supprimée.
 */
export const resolveOverlaps = createAsyncThunk(
  "sites/resolve-overlaps",
  async ({ topRing, keepSiteId }: { topRing: SiteRing; keepSiteId: string }, { dispatch, getState }) => {
    if (topRing.length < 3) return

    const state = getState() as RootState
    const sites = siteStore.selectors.listForMap(state)

    dispatch(siteStore.actions.setStatus("STATUS_SAVING"))

    for (const site of sites) {
      if (site.id === keepSiteId || site.geometryType === "line") continue

      const before = totalArea(site.geometry)
      const newGeom = subtractRing(site.geometry, topRing)

      // Zone entièrement recouverte => suppression
      if (newGeom.length === 0) {
        await siteStore.endpoints.remove(site.id)
        dispatch(siteStore.actions.remove(site.id))
        continue
      }

      // Pas de chevauchement réel (aire inchangée) => on ne touche pas
      if (Math.abs(before - totalArea(newGeom)) < MIN_AREA) continue

      const updated = { ...site, geometry: newGeom }
      await siteStore.endpoints.update(updated)
      dispatch(siteStore.actions.replace(updated))
    }

    dispatch(siteStore.actions.setStatus("STATUS_SAVED"))
  }
)

/**
 * Remove population from one site
 */
export const removePopulation = createAsyncThunk(
  "sites/remove-population",
  async ({ PopulationId, SiteId }: { PopulationId: string; SiteId: string }, { dispatch, getState }) => {
    const state = getState() as RootState
    dispatch(siteStore.actions.setStatus("STATUS_SAVING"))

    //Send data
    const response = await fetchBackend({
      url: "/site/remove-population",
      method: "POST",
      data: {
        PopulationId,
        SiteId
      }
    })

    //Handle response
    dispatch(siteStore.actions.setStatus("STATUS_SAVED"))
    if (response.error) {
      return false
    }

    //Update the store
    const site = state.site.list.find((x) => x.id === SiteId)
    if (site) {
      dispatch(
        siteStore.actions.setList(
          state.site.list.map((item) =>
            item.id === SiteId
              ? { ...item, populationIds: item.populationIds.filter((id) => id !== PopulationId) }
              : item
          )
        )
      )
    }

    return true
  }
)

/**
 * Remove risk for one site
 */
export const removeRisk = createAsyncThunk("sites/remove-risk", async (riskId: string, { dispatch, getState }) => {
  const state = getState() as RootState
  const activeSite = state.site.active

  //Save in database
  dispatch(siteStore.actions.setStatus("STATUS_SAVING"))
  const response = await fetchBackend({
    url: "/site/remove-risk",
    method: "POST",
    data: {
      RiskId: riskId,
      SiteId: activeSite.id
    }
  })

  //Handle error
  if (response.error) {
    dispatch(siteStore.actions.setStatus("STATUS_SAVED"))
    return false
  }

  //Edit store
  dispatch(physicalRiskStore.actions.setActive(null))
  return true
})

/**
 * Remove risk for one site
 */
export const updateRisk = createAsyncThunk("sites/update-risk", async (risk: PhysicalRisk, { dispatch, getState }) => {
  const state = getState() as RootState
  const activeSite = state.site.active

  //Save in database
  dispatch(siteStore.actions.setStatus("STATUS_SAVING"))
  const response = await fetchBackend({
    url: "/site/update-risk",
    method: "POST",
    data: {
      RiskId: risk.id,
      SiteId: activeSite.id
      //risk: risk.PhysicalRisksSite
    }
  })

  //Handle error
  if (response.error) {
    dispatch(siteStore.actions.setStatus("STATUS_SAVED"))
    return false
  }

  //Edit store
  dispatch(siteStore.actions.replace(response))
  dispatch(physicalRiskStore.actions.setActive(response.PhysicalRisks.find((x) => x.id === risk.id)))

  return true
})
